/**
 * Image URL utility functions
 */

import { Capacitor } from '@capacitor/core';
import { getApiBaseUrl } from '../config/api';

/** Public web origin — /uploads is proxied here, not the raw Railway API host. */
export const PUBLIC_SITE_ORIGIN = 'https://quanlynhakcn.com';

/**
 * Ensure URL uses HTTPS protocol
 * @param url - URL to normalize
 * @returns URL with https:// protocol
 */
function ensureHttps(url: string): string {
  if (url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')) {
    return url;
  }
  if (url.startsWith('http://')) {
    return url.replace(/^http:/, 'https:');
  }
  return url;
}

function uploadsPathFrom(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/uploads')) {
    return trimmed.split('#')[0];
  }
  if (trimmed.startsWith('uploads/')) {
    return `/${trimmed.split('#')[0]}`;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const parsed = new URL(trimmed);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${parsed.pathname}${parsed.search}`;
      }
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Build static file URL from path
 * - Google URLs (https://) → return as-is
 * - /uploads paths → prepend base server URL
 * - Other paths → prepend base server URL or return as-is
 * @param path - Avatar/banner path from database
 * @returns Full URL or original path
 */
export function buildStaticUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  
  // If already a full URL (http:// or https://), ensure it's HTTPS
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return ensureHttps(path);  // Google URLs and other full URLs
  }
  
  if (path.startsWith('/')) {
    const url = `${getApiBaseUrl()}${path}`;
    return ensureHttps(url);  // /uploads paths
  }
  
  return path;  // fallback
}

/**
 * Convert relative image path to full URL
 * ONLY for backend paths. For avatars, use getAvatarUrl() instead
 * @param path - Backend path or full URL
 * @returns Full URL to image
 */
export function getImageUrl(path: string | null | undefined): string {
  if (!path) {
    return '';
  }

  const uploads = uploadsPathFrom(path);
  const mediaApiPath = path.match(/\/api\/v1\/(?:store|property)-media\/\d+|\/api\/v1\/(?:store|advertising)\/media\/\d+/)
    ? path.startsWith('http')
      ? new URL(path).pathname
      : path.startsWith('/')
        ? path
        : `/${path}`
    : path.startsWith('/store/media/') || path.startsWith('/advertising/media/')
      ? `/api/v1${path}`
      : path.startsWith('/property-media/')
        ? `/api/v1${path}`
        : null;
  const appPath = uploads || mediaApiPath;
  if (appPath) {
    // Web/admin: same-origin so nginx /api and /uploads proxy work.
    if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
      return appPath;
    }
    const base = getApiBaseUrl();
    if (base.startsWith('http://localhost') || base.startsWith('http://127.0.0.1')) {
      return uploads ? `${base}${appPath}` : `http://localhost:8000${appPath}`;
    }
    return `${PUBLIC_SITE_ORIGIN}${appPath}`;
  }
  
  // If already a full URL, ensure it's HTTPS
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return ensureHttps(path);
  }
  
  // Otherwise return as is (for local assets like /img/avatar/...)
  return path;
}

/** If the first src 404s (Railway host), retry via the public domain. */
export function fallbackUploadsSrc(currentSrc: string, originalPath?: string | null): string | null {
  const source = originalPath || currentSrc;
  const uploads = uploadsPathFrom(source);
  let mediaPath: string | null = null;
  if (source.includes('/store/media/') || source.includes('/property-media/') || source.includes('/advertising/media/')) {
    try {
      const pathname = source.startsWith('http') ? new URL(source).pathname : source;
      mediaPath = pathname.startsWith('/api/v1/')
        ? pathname
        : pathname.startsWith('/store/') || pathname.startsWith('/property-media/') || pathname.startsWith('/advertising/')
          ? `/api/v1${pathname}`
          : pathname;
    } catch {
      mediaPath = null;
    }
  }
  const path = uploads || (mediaPath && mediaPath.startsWith('/') ? mediaPath : null);
  if (!path) return null;
  const fallback = `${PUBLIC_SITE_ORIGIN}${path}`;
  if (currentSrc === fallback || (currentSrc.endsWith(path) && currentSrc.startsWith(PUBLIC_SITE_ORIGIN))) {
    return null;
  }
  return fallback;
}

/**
 * Generate initials from name
 * @param name - Full name (e.g., "John Doe" -> "JD")
 * @returns Initials (max 2 characters)
 */
export function getInitials(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return '?';
  
  const parts = name.trim().split(/\s+/).filter(p => p.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  
  // Take first letter of first and last name
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Generate avatar SVG with initials
 * @param name - User name
 * @param size - Avatar size in pixels
 * @returns SVG data URL
 */
export function generateAvatarSvg(name: string | null | undefined, size: number = 200): string {
  const initials = getInitials(name);
  
  // Color palette - consistent with app theme
  const colors = [
    '#C4A35A', // Earth yellow (primary)
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#FFA07A', // Light salmon
    '#98D8C8', // Light green
    '#F7DC6F', // Yellow
    '#BB8FCE', // Purple
  ];
  
  // Use name hash to pick consistent color
  const hash = (name || '').split('').reduce((h, char) => h + char.charCodeAt(0), 0);
  const bgColor = colors[hash % colors.length];
  
  const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${bgColor}"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="Arial, sans-serif" font-size="${size * 0.35}" font-weight="bold" fill="white">${initials}</text></svg>`;
  
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Get avatar URL with initials fallback
 * - Database avatar (/uploads/...) → convert to full URL via getImageUrl()
 * - Google avatar (https://...) → return as is
 * - No avatar + has name → generate initials SVG
 */
export function getAvatarUrl(
  avatar: string | null | undefined,
  name: string | null | undefined
): string {
  // Try to get valid image URL from avatar
  const url = getImageUrl(avatar);
  
  // If we got a valid URL, return it
  if (url) return url;
  
  // If no valid avatar URL, always use name (user always has name)
  return generateAvatarSvg(name);
}

/**
 * Ảnh bìa hồ sơ cố định — không cho user đổi.
 */
export const FIXED_PROFILE_BANNER = '/img/slider/Anhslider1.png';

/**
 * Get banner URL. Always the same cover image.
 */
export function getBannerUrl(_banner?: string | null, _fallback?: string): string {
  return FIXED_PROFILE_BANNER;
}

